import type { RequestListItem } from '../../lib/api';

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    if (hours < 1) return 'только что';
    if (hours < 24) return `${hours} ч назад`;
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн. назад`;
    return 'неделю назад';
  } catch {
    return '';
  }
}

function typeBadgeClass(type: string): string {
  const t = type.toLowerCase();
  if (t === 'development' || t.includes('разработ')) return 'request-card__badge request-card__badge--dev';
  if (t === 'partner' || t.includes('партн')) return 'request-card__badge request-card__badge--partner';
  if (t === 'investor' || t === 'invest' || t.includes('инвест')) return 'request-card__badge request-card__badge--invest';
  return 'request-card__badge';
}

interface RequestCardProps {
  request: RequestListItem;
  onClick?: () => void;
  onRespond?: (id: string) => void;
  actionLabel?: string | null;
  onAction?: (id: string) => void | Promise<void>;
  actionDisabled?: boolean;
  hideDefaultAction?: boolean;
}

function statusLabel(status: string): string {
  const s = status.toLowerCase();
  if (s === 'draft') return 'Черновик';
  if (s === 'submitted') return 'На модерации';
  if (s === 'approved') return 'Одобрено';
  if (s === 'rejected') return 'Отклонено';
  if (s === 'needs_fix') return 'Нужны правки';
  return status;
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('button, a, input, textarea, select'));
}

export function RequestCard({
  request,
  onClick,
  onRespond,
  actionLabel,
  onAction,
  actionDisabled = false,
  hideDefaultAction = false,
}: RequestCardProps) {
  const responded = request.i_responded === true;
  const clickable = typeof onClick === 'function';
  const moderationNote =
    request.status === 'rejected'
      ? request.rejected_reason
      : request.status === 'needs_fix'
        ? request.needs_fix_comment
        : null;

  const handleRespondClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!responded && onRespond) onRespond(request.id);
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAction && !actionDisabled) onAction(request.id);
  };

  return (
    <article
      className="request-card"
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : -1}
      onClick={(e) => {
        if (!clickable || isInteractiveTarget(e.target)) return;
        onClick?.();
      }}
      onKeyDown={(e) => {
        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      aria-label={`Заявка: ${request.title}`}
    >
      <div className="request-card__header">
        <span className="request-card__avatar" aria-hidden>
          {request.author?.username?.[0]?.toUpperCase() ?? '?'}
        </span>
        <div className="request-card__title-wrap">
          <h3 className="request-card__title">{request.title}</h3>
          <p className="request-card__meta">
            @{request.author?.username ?? 'автор'} · {formatTime(request.published_at)}
          </p>
        </div>
        <span className={typeBadgeClass(request.type)}>{request.type_label}</span>
      </div>
      <p className="request-card__description">{request.description}</p>
      {request.tools.length > 0 && (
        <div className="request-card__tags">
          {request.tools.slice(0, 4).map((t) => (
            <span key={t} className="request-card__tag">
              {t}
            </span>
          ))}
        </div>
      )}
      <div className="request-card__footer">
        {request.urgent && (
          <span className="request-card__urgent">Срочно</span>
        )}
        <span className="request-card__deadline">{statusLabel(request.status)}</span>
        {request.budget && (
          <span className="request-card__budget">{request.budget}</span>
        )}
        {request.deadline && (
          <span className="request-card__deadline">{request.deadline}</span>
        )}
        {request.format && (
          <span className="request-card__format">{request.format}</span>
        )}
      </div>
      {moderationNote && <p className="request-card__moderation-note">! {moderationNote}</p>}
      {(actionLabel && onAction) || responded || (!hideDefaultAction && onRespond) ? (
        <div className="request-card__action">
          {actionLabel && onAction ? (
          <button
            type="button"
            className="request-card__btn-respond"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleActionClick}
            disabled={actionDisabled}
          >
            {actionLabel}
          </button>
          ) : responded ? (
            <span className="request-card__responded">✓ Вы уже откликнулись</span>
          ) : (
          <button
            type="button"
            className="request-card__btn-respond"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleRespondClick}
            disabled={!onRespond}
          >
            Откликнуться
          </button>
          )}
        </div>
      ) : null}
    </article>
  );
}
